import {
  DefaultTransformer,
  deserialize,
  DocumentObject,
  JsonObject,
  serialize,
  transform,
  Transformer,
  whitelist,
} from '@jsonapiworld/jsonapi-serializer';
import axios from 'axios';
import filter from 'lodash/filter';
import { backendApiGateway } from '~/client/backend';
import {
  Company,
  CompanyResourceType,
  CompanyUser,
  CreateCompanyPayload,
  InviteUserRequest,
  UserRole,
} from '~/types/company';
import { buildJsonAPIQueryParams, jsonAPIFilterBuilder, jsonAPIIncludeBuilder } from '~/utils/jsonapi-query';

class CreateCompanyTransformer extends Transformer<CreateCompanyPayload, {}> {
  constructor() {
    super();
    this.type = CompanyResourceType;
    this.relationships = {
      companyIdentity: this.companyIdentityRelationship(),
    };
  }

  transform(payload: CreateCompanyPayload): JsonObject {
    const relationships = ['companyIdentity'];
    const keys = filter(Object.keys(payload), (val) => !relationships.includes(val));
    return whitelist(payload, keys);
  }

  companyIdentityRelationship() {
    return function (payload: CreateCompanyPayload) {
      const included = Object.entries(payload.companyIdentity).length > 1;
      return transform()
        .withInput(payload.companyIdentity)
        .withTransformer(new DefaultTransformer('company-identity'))
        .withIncluded(included)
        .toContext();
    };
  }
}

export const fetchCompany = async (companyId: string) => {
  const includeBuilder = jsonAPIIncludeBuilder(['companyIdentity', 'companySettings']);
  const filterBuider = jsonAPIFilterBuilder();
  filterBuider.eq('id', companyId);
  const params = buildJsonAPIQueryParams(includeBuilder, filterBuider);

  const { data } = await backendApiGateway.get<DocumentObject & { meta: { has_documents: boolean } }>(
    `/api/v1/companies/${companyId}`,
    {
      params,
    },
  );

  const meta = data.meta as { has_documents: boolean } | undefined;
  const company = deserialize<Company>(data) as Company;

  return { company, hasDocuments: meta?.has_documents ?? false };
};

export const createCompany = async (values: CreateCompanyPayload, token?: string) => {
  const body = transform<CreateCompanyPayload, JsonObject>()
    .withInput(values)
    .withTransformer(new CreateCompanyTransformer())
    .serialize();
  const { data } = await backendApiGateway.post<DocumentObject>('/api/v1/companies', body, {
    params: token ? { token } : undefined,
  });

  const deserialized = deserialize<Company>(data) as Company;
  return deserialized;
};

export const createSecondCompany = async (
  values: Omit<CreateCompanyPayload, 'leadSource'> & { currentCompanyUuid: string },
) => {
  const { currentCompanyUuid, ...others } = values;

  const companyIdentity =
    'id' in others.companyIdentity
      ? { id: others.companyIdentity.id }
      : {
          address: others.companyIdentity.address,
          city: others.companyIdentity.city,
          country: others.companyIdentity.country,
          address_country: others.companyIdentity.addressCountry,
          postal_code: others.companyIdentity.postalCode,
          vat_number: others.companyIdentity.vatNumber,
          tax_number: others.companyIdentity.taxNumber,
          company_name: others.companyIdentity.companyName,
        };

  const { data } = await backendApiGateway.post<DocumentObject>(
    '/api/v1/companies/create-second-company',
    {
      current_company_id: currentCompanyUuid,
      data: {
        tax_number: others.taxNumber,
        vat_number: others.vatNumber,
        name: others.name,
        fiscal_regime: others.fiscalRegime,
        company_identity: companyIdentity,
      },
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  const deserialized = deserialize<Company>(data) as Company;
  return deserialized;
};

export const updateCompany = async (id: Company['id'], values: Partial<Company>) => {
  const payload = serialize(
    {
      id,
      ...values,
    },
    'company',
  );

  await backendApiGateway.patch<DocumentObject>(`/api/v1/companies/${id}`, payload);
};

export const joinCompany = async (value: { taxNumber: string; userRole: UserRole }) => {
  const { data } = await backendApiGateway.post<{ company: string }>('/api/v1/companies/join', value, {
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return data;
};

export const uploadLogo = async (file: File, companyId: Company['id']) => {
  const {
    data: { url },
  } = await backendApiGateway.post<{ url: string }>(`/api/v1/companies/logo_url/${companyId}`, {
    filename: file.name,
    contentType: file.type,
  });

  await axios.put(url, file, { headers: { 'Content-Type': file.type } });

  const payload = serialize({ logo: file.name }, CompanyResourceType);

  await backendApiGateway.patch(`/api/v1/companies/${companyId}`, payload);
};

export const inviteUser = async (inviteUserRequest: InviteUserRequest) => {
  const { data } = await backendApiGateway.post<DocumentObject>(
    `/api/v1/companies/${inviteUserRequest.companyId}/invite-user`,
    inviteUserRequest.data,
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  const deserialized = deserialize<CompanyUser>(data) as CompanyUser;
  return deserialized;
};
